type XY = number[];

export function inRing(point: XY, ring: XY[]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [x, y] = point;
    const a = ring[i], b = ring[j];
    if ((a[1] > y) !== (b[1] > y) && x < (b[0] - a[0]) * (y - a[1]) / (b[1] - a[1]) + a[0]) inside = !inside;
  }
  return inside;
}

export function containsPoint(point: XY, geometry: GeoJSON.Geometry): boolean {
  const polygon = (rings: XY[][]) => inRing(point, rings[0]) && !rings.slice(1).some((ring) => inRing(point, ring));
  if (geometry.type === 'Polygon') return polygon(geometry.coordinates);
  if (geometry.type === 'MultiPolygon') return geometry.coordinates.some(polygon);
  return false;
}

export function positions(geometry: GeoJSON.Geometry): XY[] {
  switch (geometry.type) {
    case 'Point': return [geometry.coordinates];
    case 'MultiPoint':
    case 'LineString': return geometry.coordinates;
    case 'MultiLineString':
    case 'Polygon': return geometry.coordinates.flat();
    case 'MultiPolygon': return geometry.coordinates.flat(2);
    case 'GeometryCollection': return geometry.geometries.flatMap(positions);
  }
}

export function centerOf(geometry: GeoJSON.Geometry): [number, number] {
  const points = positions(geometry);
  return [
    (Math.min(...points.map((p) => p[0])) + Math.max(...points.map((p) => p[0]))) / 2,
    (Math.min(...points.map((p) => p[1])) + Math.max(...points.map((p) => p[1]))) / 2,
  ];
}

function segmentsCross(a: XY, b: XY, c: XY, d: XY) {
  const cross = (p: XY, q: XY, r: XY) => (q[0] - p[0]) * (r[1] - p[1]) - (q[1] - p[1]) * (r[0] - p[0]);
  return cross(a, b, c) * cross(a, b, d) < 0 && cross(c, d, a) * cross(c, d, b) < 0;
}

export function intersectsArea(geometry: GeoJSON.Geometry, ring: XY[]): boolean {
  const lineIntersects = (line: XY[]) => line.some((point) => inRing(point, ring))
    || line.some((point, index) => index > 0 && ring.some((edge, j) =>
      segmentsCross(line[index - 1], point, edge, ring[(j + 1) % ring.length])));
  const polygonIntersects = (rings: XY[][]) => rings.some(lineIntersects)
    || ring.some((point) => inRing(point, rings[0]) && !rings.slice(1).some((hole) => inRing(point, hole)));

  switch (geometry.type) {
    case 'Point': return inRing(geometry.coordinates, ring);
    case 'MultiPoint': return geometry.coordinates.some((point) => inRing(point, ring));
    case 'LineString': return lineIntersects(geometry.coordinates);
    case 'MultiLineString': return geometry.coordinates.some(lineIntersects);
    case 'Polygon': return polygonIntersects(geometry.coordinates);
    case 'MultiPolygon': return geometry.coordinates.some(polygonIntersects);
    case 'GeometryCollection': return geometry.geometries.some((item) => intersectsArea(item, ring));
  }
}
